import type { AxiosInstance } from 'axios';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { environment } from '../config/environment';
import { useAuth } from '../context/AuthContext';
import { createApiClient } from './api-client.unified';

// Mock handlers registry
const mockHandlers: Record<string, (params?: URLSearchParams) => Promise<any>> = {};

// Register a mock handler for a specific endpoint
export const registerMockHandler = (endpoint: string, handler: (params?: URLSearchParams) => Promise<any>) => {
    mockHandlers[endpoint] = handler;
};

// Helper to get base endpoint without query params
const getBaseEndpoint = (url: string | undefined): string => {
    if (!url) return '';
    const [baseUrl] = url.split('?');
    return baseUrl.replace(environment.ctLiteApiUrl, '').replace(environment.expressApiUrl, '');
};

export const useApiClient = () => {
    const { logout } = useAuth();
    const apiClientRef = useRef<AxiosInstance | null>(null);
    const navigate = useNavigate();

    // Create the apiClient instance once
    if (!apiClientRef.current) {
        const client = createApiClient();

        // Add mock interceptor if mock mode is enabled
        if (environment.useMockApi) {
            client.interceptors.request.use(async (config) => {
                const endpoint = getBaseEndpoint(config.url);
                if (endpoint && mockHandlers[endpoint]) {
                    let params: URLSearchParams | undefined;
                    if (config.params instanceof URLSearchParams) {
                        params = config.params;
                    } else if (config.params) {
                        params = new URLSearchParams();
                        Object.entries(config.params).forEach(([key, value]) => {
                            if (Array.isArray(value)) {
                                if (value.length > 0) {
                                    value.forEach((v) => params?.append(key, v.toString()));
                                }
                            } else if (value !== undefined && value !== null && value !== '') {
                                params?.append(key, value.toString());
                            }
                        });
                    }

                    if (params && Array.from(params.entries()).length === 0) {
                        params = undefined;
                    }

                    console.log('Mock API Request:', {
                        endpoint,
                        params: params ? Object.fromEntries(params.entries()) : 'none',
                    });

                    config.adapter = async () => {
                        const response = await mockHandlers[endpoint](params);
                        return {
                            data: response,
                            status: 200,
                            statusText: 'OK',
                            headers: {},
                            config,
                        };
                    };
                }
                return config;
            });
        }

        apiClientRef.current = client;
    }

    useEffect(() => {
        const apiClient = apiClientRef.current;
        if (!apiClient) return;

        // Attach response interceptor to handle 401/403 errors
        const responseInterceptor = apiClient.interceptors.response.use(
            (response) => response,
            async (error) => {
                // Handle authentication errors - redirect to CoinTracking login
                if (error.response?.status === 401 || error.response?.status === 403) {
                    console.log('[API Interceptor] 401/403 Error detected:', {
                        url: error.config?.url,
                        method: error.config?.method,
                        status: error.response?.status,
                        data: error.response?.data
                    });
                    // logout();
                    // logout() will redirect to CT login
                    return Promise.reject(error);
                }

                return Promise.reject(error);
            }
        );

        // Cleanup interceptors on unmount
        return () => {
            apiClient.interceptors.response.eject(responseInterceptor);
        };
    }, [logout, navigate]);

    return apiClientRef.current;
};
